import { toLocalDate as toDate } from '@pazportal/ui';

/** Em dash used everywhere a value is genuinely absent. */
export const EM_DASH = '—';

const SOLUTION_LABELS: Record<string, string> = {
  managed_network: 'Managed Network Solutions',
  video_surveillance: 'Surveillance Solutions',
  access_control: 'Access Control',
  pro_av: 'Pro AV',
  digital_signage: 'Digital Signage',
  security_alarm: 'Security / Alarm',
  smart_robotics: 'Smart Robotics Solutions',
  keytracer: 'Security Solutions (KeyTracer)',
  it_cloud: 'IT & Cloud',
  pos_systems: 'Retail Solutions (POS)',
  conferencing: 'Conferencing Solutions',
  audio: 'Audio Solutions',
};

/** Canonical ordering for solution-type tabs and lists. */
export const SOLUTION_TYPE_ORDER = Object.keys(SOLUTION_LABELS);

export function formatSolutionType(type: string | null | undefined): string {
  if (!type) return EM_DASH;
  return SOLUTION_LABELS[type] ?? type.replace(/_/g, ' ');
}

export function formatDate(dateStr: string | null | undefined): string {
  const d = toDate(dateStr);
  if (!d) return EM_DASH;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function formatDateTime(dateStr: string | null | undefined): string {
  const d = toDate(dateStr);
  if (!d) return EM_DASH;
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

export function timeAgo(dateStr: string | null | undefined): string {
  const d = toDate(dateStr);
  if (!d) return EM_DASH;
  const diffMs = Date.now() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHour < 24) return `${diffHour}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function daysUntil(dateStr: string | null | undefined): number | null {
  const d = toDate(dateStr);
  if (!d) return null;
  return Math.ceil((d.getTime() - Date.now()) / 86_400_000);
}

/**
 * locations.address is free-form JSON. Join the fields we recognise; return
 * null when nothing usable is present so callers can show an em dash.
 */
export function formatAddress(address: unknown): string | null {
  if (!address || typeof address !== 'object') return null;
  const a = address as Record<string, unknown>;
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const v = a[k];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
    return null;
  };
  const line1 = pick('line1', 'street', 'address1', 'address_line1', 'street1');
  const line2 = pick('line2', 'address2', 'address_line2', 'street2');
  const city = pick('city', 'locality');
  const region = pick('state', 'region', 'province');
  const postal = pick('postal_code', 'zip', 'postcode', 'zip_code');
  const cityLine = [city, [region, postal].filter(Boolean).join(' ')].filter(Boolean).join(', ');
  const parts = [line1, line2, cityLine].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** 'in_progress' → 'In progress'. For prose; StatusBadge does its own casing. */
export function humanize(value: string | null | undefined): string {
  if (!value) return EM_DASH;
  const words = value.replace(/[_-]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

/** 'Jose Vinueza' → 'JV', 'jose@paznetworks.com' → 'JO'. */
export function initialsFrom(value: string | null | undefined): string {
  if (!value) return '??';
  const name = value.includes('@') ? value.split('@')[0] : value;
  const parts = name.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.length >= 2 ? parts[0][0] + parts[1][0] : name.slice(0, 2);
  return letters.toUpperCase();
}
